import { z } from 'zod';
import { type Paise, assertPaise, percentOf, roundToPaise, sumPaise } from './paise.js';

/**
 * GST is OPTIONAL per shop: a large share of this market is below the ₹40 lakh threshold
 * or on the composition scheme and must not print tax lines at all. These helpers compute
 * tax; whether a document shows it is a shop setting (task 18).
 */

/** GST rates that apply to tailoring goods and job work. */
export const GST_RATES = [0, 5, 12, 18, 28] as const;
export type GstRate = (typeof GST_RATES)[number];

export const gstRateSchema = z.union([
  z.literal(0),
  z.literal(5),
  z.literal(12),
  z.literal(18),
  z.literal(28),
]);

/** 15 characters: 2 state code, 10 PAN, 1 entity, 1 'Z', 1 checksum. */
export const gstinSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, 'Not a valid GSTIN');

/** Harmonised codes for goods (HSN) and services (SAC), 4 to 8 digits. */
export const hsnSacSchema = z.string().regex(/^\d{4,8}$/, 'HSN/SAC must be 4 to 8 digits');

export interface GstInput {
  /** Line amount in paise — tax-exclusive, or tax-inclusive when `inclusive` is true. */
  readonly amountPaise: Paise;
  readonly ratePercent: number;
  /** True when `amountPaise` already contains the tax. */
  readonly inclusive?: boolean;
  /**
   * True when the place of supply is outside the shop's own state: the tax becomes a
   * single IGST line instead of a CGST + SGST pair.
   */
  readonly interState?: boolean;
}

export interface GstBreakdown {
  readonly ratePercent: number;
  /** The amount tax is computed on. */
  readonly taxablePaise: Paise;
  readonly cgstPaise: Paise;
  readonly sgstPaise: Paise;
  readonly igstPaise: Paise;
  /** cgst + sgst + igst — always exactly this, never a re-rounded figure. */
  readonly taxPaise: Paise;
  /** taxable + tax. */
  readonly totalPaise: Paise;
  readonly interState: boolean;
}

/**
 * Compute the tax on one amount. The invariants that must always hold:
 *   cgst + sgst + igst === tax   and   taxable + tax === total
 * For an inclusive price, `total` is exactly the price the shop quoted — the split is
 * derived from it, so a quoted ₹1,000 blouse never becomes ₹1,000.01 on the bill.
 */
export function computeGst({
  amountPaise,
  ratePercent,
  inclusive = false,
  interState = false,
}: GstInput): GstBreakdown {
  assertPaise(amountPaise);
  if (!Number.isFinite(ratePercent) || ratePercent < 0) {
    throw new RangeError(`Invalid GST rate: ${ratePercent}`);
  }

  let taxablePaise: Paise;
  let taxPaise: Paise;
  let totalPaise: Paise;

  if (inclusive) {
    taxablePaise = roundToPaise((amountPaise * 100) / (100 + ratePercent));
    taxPaise = amountPaise - taxablePaise;
    totalPaise = amountPaise;
  } else {
    taxablePaise = amountPaise;
    taxPaise = percentOf(amountPaise, ratePercent);
    totalPaise = taxablePaise + taxPaise;
  }

  if (interState) {
    return {
      ratePercent,
      taxablePaise,
      cgstPaise: 0,
      sgstPaise: 0,
      igstPaise: taxPaise,
      taxPaise,
      totalPaise,
      interState: true,
    };
  }

  // Half each, with the remainder landing on CGST so the pair always sums back to the tax.
  const cgstPaise = roundToPaise(taxPaise / 2);
  const sgstPaise = taxPaise - cgstPaise;
  return {
    ratePercent,
    taxablePaise,
    cgstPaise,
    sgstPaise,
    igstPaise: 0,
    taxPaise,
    totalPaise,
    interState: false,
  };
}

/** Roll several line breakdowns up into one document-level total. */
export function sumGstBreakdowns(
  breakdowns: readonly GstBreakdown[],
): Omit<GstBreakdown, 'ratePercent'> & { readonly ratePercent: number | null } {
  const interState = breakdowns.some((breakdown) => breakdown.interState);
  const rates = new Set(breakdowns.map((breakdown) => breakdown.ratePercent));
  return {
    ratePercent: rates.size === 1 ? (breakdowns[0]?.ratePercent ?? null) : null,
    taxablePaise: sumPaise(breakdowns.map((breakdown) => breakdown.taxablePaise)),
    cgstPaise: sumPaise(breakdowns.map((breakdown) => breakdown.cgstPaise)),
    sgstPaise: sumPaise(breakdowns.map((breakdown) => breakdown.sgstPaise)),
    igstPaise: sumPaise(breakdowns.map((breakdown) => breakdown.igstPaise)),
    taxPaise: sumPaise(breakdowns.map((breakdown) => breakdown.taxPaise)),
    totalPaise: sumPaise(breakdowns.map((breakdown) => breakdown.totalPaise)),
    interState,
  };
}

/** Group breakdowns by rate — the rate-wise summary every tax invoice must print. */
export function gstSummaryByRate(breakdowns: readonly GstBreakdown[]): GstBreakdown[] {
  const byRate = new Map<number, GstBreakdown[]>();
  for (const breakdown of breakdowns) {
    const bucket = byRate.get(breakdown.ratePercent);
    if (bucket) bucket.push(breakdown);
    else byRate.set(breakdown.ratePercent, [breakdown]);
  }
  return [...byRate.entries()]
    .sort(([a], [b]) => a - b)
    .map(([ratePercent, group]) => ({ ...sumGstBreakdowns(group), ratePercent }));
}
